import { useState, useEffect, useCallback } from "react";
import service from "../../services/Service.js";
import { Button, List, Modal, Input, DatePicker, message, notification } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, CopyOutlined, CheckOutlined } from "@ant-design/icons";
import withNavigation from '../hooks/withNavigation.jsx';
import { withLocale } from "../../l10n/index.jsx";
import dayjs from 'dayjs';
import { formatDate } from '../../utils/dates';

function ApiKeyList({ user_uuid, lang }) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({
    modalVisible: false,
    showKeyModal: false,
    editing: false,
    loading: false,
  });

  const [data, setData] = useState({
    tokens: [],
    token: {
      uuid: "",
      name: "",
      description: "",
      expire_date: null,
    },
  });

  const [security, setSecurity] = useState({
    generatedKey: "",
  });

  // ===== 2. HELPERS (Para actualizar estado) =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateData = (field, value) => setData(prev => ({ ...prev, [field]: value }));
  const updateSecurity = (field, value) => setSecurity(prev => ({ ...prev, [field]: value }));

  const updateToken = (field, value) => {
    setData(prev => ({
      ...prev,
      token: {
        ...prev.token,
        [field]: value,
      }
    }));
  };

  const generateSecureApiKey = (length = 32) => {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('').slice(0, length);
  };

  // ===== 3. FUNCIONES PRINCIPALES =====
  const getListData = useCallback(async () => {
    try {
      const res = await service.get(`users/${user_uuid}/apikeys`);
      if (res.data.ok === false) {
        notification.error({
          message: lang.handle(res.data.message || "Error al obtener datos"),
        });
        updateData('tokens', []);
      } else {
        updateData('tokens', res.data);
      }
    } catch (err) {
      console.error("Error al obtener lista de claves:", err);
      notification.error({
        message: lang.handle("Error al cargar las claves API"),
      });
      updateData('tokens', []);
    }
  }, [user_uuid, lang]);

  // ===== 4. VALIDACIONES =====
  const validateToken = (tokenData, isEditing = false) => {
    const { name, expire_date } = tokenData;

    if (!name || !name.trim() || !expire_date || !dayjs(expire_date).isValid()) {
      message.warning(lang.handle("El nombre y la fecha de expiración son obligatorios"));
      return false;
    }

    if (!isEditing && expire_date.isBefore(dayjs(), 'day')) {
      message.warning(lang.handle("La fecha de expiración debe ser posterior a la fecha actual"));
      return false;
    }

    return true;
  };

  // ===== 5. HANDLERS =====
  const onOpenNewApiKeyModal = () => {
    setUI(prev => ({ ...prev, editing: false, modalVisible: true }));
    updateData('token', { uuid: "", name: "", description: "", expire_date: null });
  };

  const onCloseNewApiKeyModal = () => {
    setUI(prev => ({ ...prev, editing: false, modalVisible: false }));
    updateData('token', { uuid: "", name: "", description: "", expire_date: null });
  };

  const onOpenEditApiKeyModal = (api_key) => {
    updateData('token', {
      uuid: api_key.uuid,
      name: api_key.name,
      description: api_key.description,
      expire_date: dayjs(api_key.expire_date, "YYYY-MM-DD"),
    });
    setUI(prev => ({ ...prev, editing: true, modalVisible: true }));
  };

  const onSaveApiKey = async () => {
    if (!validateToken(data.token, ui.editing)) {
      return;
    }

    updateUI('loading', true);

    try {
      if (ui.editing) {
        const updatepayload = { uuid: data.token.uuid, name: data.token.name, description: data.token.description };
        const res = await service.put(`users/${user_uuid}/apikeys/${data.token.uuid}`, updatepayload);
        if (res.data.ok === false) {
          notification.error({ 
            message: lang.handle(res.data.message || "Error al editar la clave") 
          });
          return;
        }
        notification.success({ message: lang.handle('Clave editada correctamente') });
        onCloseNewApiKeyModal();
      } else {
        const apiKeyGenerated = generateSecureApiKey();
        const newToken = {
          api_key: apiKeyGenerated,
          name: data.token.name,
          description: data.token.description,
          owner_uuid: user_uuid,
          expire_date: data.token.expire_date.format("DD-MM-YYYY"),
        };
        const res = await service.post(`users/${user_uuid}/apikeys`, newToken);
        if (res.data.ok === false) {
          notification.error({ 
            message: lang.handle(res.data.message || "Error al crear la clave") 
          });
          return;
        }
        notification.success({ message: lang.handle('Clave creada correctamente') });
        updateSecurity('generatedKey', apiKeyGenerated);
        updateUI('showKeyModal', true);
        onCloseNewApiKeyModal();
      }
      await getListData();
    } catch (err) {
      console.error('Error al guardar clave:', err);
      notification.error({ 
        message: lang.handle("Error al guardar la clave") 
      });
    } finally {
      updateUI('loading', false);
    }
  };

  const onDelete = (api_key) => {
    Modal.confirm({
      centered: true,
      title: lang.handle('¿Deseas borrar esta clave API?'),
      okText: lang.handle('Sí, borrar'),
      cancelText: lang.handle('Cancelar'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await service.del(`users/${user_uuid}/apikeys/${api_key.uuid}`);
          if (res.data.ok === false) {
            notification.error({ 
              message: lang.handle(res.data.message || "Error al borrar la clave") 
            });
          } else {
            notification.success({ message: lang.handle('Clave borrada correctamente') });
            await getListData();
          }
        } catch (err) {
          console.error('Error al borrar clave:', err);
          notification.error({ 
            message: lang.handle("Error al borrar la clave") 
          });
        }
      }
    });
  };

  const onCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(security.generatedKey);
      message.success(lang.handle('Clave copiada al portapapeles'));
    } catch (err) {
      console.error('Error al copiar la clave:', err);
      message.error(lang.handle('Error al copiar la clave'));
    }
  };

  const onCloseKeyModal = () => {
    Modal.confirm({
      centered: true,
      title: lang.handle('¿Estás seguro?'),
      content: lang.handle('No podrás volver a ver esta clave API. ¿Deseas continuar?'),
      okText: lang.handle('Sí, cerrar'),
      cancelText: lang.handle('Cancelar'),
      okButtonProps: { danger: true },
      onOk: () => {
        updateUI('showKeyModal', false);
        updateSecurity('generatedKey', "");
        message.info(lang.handle('La clave no volverá a estar disponible'));
      }
    });
  };

  // ===== 6. EFFECTS =====
  useEffect(() => {
    if (user_uuid) {
      getListData();
    }
  }, [user_uuid, getListData]);

  // ===== 7. RENDER =====
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <h4 className="card-title p-3">
          {lang.handle('Listado de claves API')}
        </h4>

        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={onOpenNewApiKeyModal}
        >
          {lang.handle('Generar nueva clave')}
        </Button>
      </div>

      <div style={{ border: "1px solid #d9d9d9", borderRadius: "6px", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <List
          pagination={{
            pageSize: 10,
            align: "center",
          }}
          itemLayout="horizontal"
          dataSource={data.tokens}
          renderItem={(item) => (
            <List.Item
              key={item.uuid}
              actions={[
                <Button
                  key="edit"
                  type="default"
                  icon={<EditOutlined />}
                  onClick={() => onOpenEditApiKeyModal(item)}
                >
                  {lang.handle('Editar')}
                </Button>,
                <Button
                  key="delete"
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDelete(item)}
                >
                  {lang.handle('Borrar')}
                </Button>
              ]}
            >
              <List.Item.Meta
                title={item.name}
                description={
                  <>
                    {item.description ? item.description : <em>{lang.handle('Sin descripción')}</em>}
                    <br />
                    {lang.handle('Fecha de expiración')}: <strong>{formatDate(item.expire_date)}</strong>
                  </>
                }
            />
          </List.Item>
          )}
        />
      </div>

      <Modal
        title={lang.handle(ui.editing ? 'Editar clave API' : 'Nueva clave API')}
        open={ui.modalVisible}
        centered={true}
        closable={false}
        onOk={onSaveApiKey}
        onCancel={onCloseNewApiKeyModal}
        okText={lang.handle('Guardar')}
        cancelText={lang.handle('Cancelar')}
        confirmLoading={ui.loading}
      >
        <Input
          placeholder={lang.handle('Nombre')}
          value={data.token.name}
          onChange={(e) => updateToken("name", e.target.value)}
          className="mb-3"
          maxLength={100}
        />
        <Input.TextArea
          placeholder={lang.handle('Descripción')}
          value={data.token.description}
          onChange={(e) => updateToken("description", e.target.value)}
          rows={3}
          className="mb-3"
          maxLength={500}
        />
        <DatePicker
          style={{ width: "100%" }}
          placeholder={lang.handle('Fecha de expiración')}
          value={data.token.expire_date}
          disabledDate={(current) => current && current <= dayjs().endOf('day')}
          onChange={(date) => updateToken("expire_date", date)}
          disabled={ui.editing}
        />
        {ui.editing && (
          <p style={{ marginTop: "8px", fontSize: "12px", color: "#999" }}>
            {lang.handle('No puedes cambiar la fecha de expiración una vez creada')}
          </p>
        )}
      </Modal>

      <Modal
        open={ui.showKeyModal}
        closable={false}
        footer={[
          <Button key="copy" type="primary" icon={<CopyOutlined />} onClick={onCopyToClipboard}>
            {lang.handle('Copiar clave')}
          </Button>,
          <Button key="close" danger onClick={onCloseKeyModal}>
            {lang.handle('Cerrar')}
          </Button>
        ]}
        style={{ borderTop: "5px solid rgba(161, 222, 130, 0.99)" }}
        centered
      >
        <h3 style={{ color: "#52c41a" }}><CheckOutlined /> {lang.handle('Clave API creada correctamente')}</h3>
        <p>{lang.handle('Esta es tu clave API. No podrás volver a consultarla en el futuro, cópiala y úsala donde necesites')}</p>
        <Input.TextArea
          value={security.generatedKey}
          readOnly
          autoSize={{ minRows: 2 }}
        />
      </Modal>
    </div>
  );
}

export default withLocale(withNavigation(ApiKeyList));
